import {useMemo} from "react";
import {Navigate, useParams} from "react-router-dom";
import {Loading} from "../../components/loading-data/LoadingData";
import {Page} from "../../components/page/Page";
import {AppPages, usePageUrl} from "../../app-layout/AppPages";
import {FloorPlanViewer} from "./FloorPlanViewer";
import {useDeskStore} from "../../api/DeskStore";
import {RemoteValue} from "../../util/RemoteValue";
import {useDeskModelStore} from "../../api/DeskModelStore";
import {DateRangeOptions} from "../../components/filter-fields/DateRangeFilter";
import {FloorModel} from "./FloorModel";
import {HeaderBreadcrumb, HeaderBreadcrumbs} from "../../components/page/HeaderBreadcrumbs";

function FloorBreadcrumbs({floors, selectedFloor}: {
    floors: readonly FloorModel[],
    selectedFloor: FloorModel,
}) {
    const pageUrl = usePageUrl();

    const breadcrumbs: HeaderBreadcrumb[] = useMemo(() => [
        HeaderBreadcrumbs.link('Floors', pageUrl(AppPages.floors)),
        floors.length > 1 ? (
            HeaderBreadcrumbs.menu(
                selectedFloor,
                floors,
                floor => ({
                    key: floor.floor.id,
                    label: floor.floor.name,
                    to: pageUrl(AppPages.floor, {floorId: floor.floor.id}),
                })
            )
        ) : (
            HeaderBreadcrumbs.text(selectedFloor.floor.name)
        ),
    ], [floors, selectedFloor, pageUrl]);
    return (
        <HeaderBreadcrumbs breadcrumbs={breadcrumbs}/>
    );
}

export default function FloorPage() {
    const {floorId} = useParams();
    const desks = useDeskStore(state => state.remoteValue);
    const deskModel = useDeskModelStore(DateRangeOptions.lastFourMonths);
    const pageUrl = usePageUrl();

    const floorPageModel = useMemo(
        () => RemoteValue
            .combine({deskModel, desks})
            .map(({deskModel, desks}) => {
                if (floorId == null) return undefined;
                const floors = FloorModel.allFloors(deskModel, desks);
                const selectedFloor = floors.find(floor => floor.floor.id === floorId);
                return selectedFloor ? {selectedFloor, floors} : undefined;
            }),
        [deskModel, floorId, desks],
    );

    return (
        <Loading value={floorPageModel}>{resolved => resolved ? (
            <Page header={<FloorBreadcrumbs floors={resolved.floors} selectedFloor={resolved.selectedFloor}/>}>
                <FloorPlanViewer
                    src={resolved.selectedFloor.floor.floorPlanUrl}
                    alt={`Floor plan for ${resolved.selectedFloor.floor.name}`}
                    floor={resolved.selectedFloor}
                />
            </Page>
        ) : (
            <Navigate to={pageUrl(AppPages.floors)} replace/>
        )}</Loading>
    );
}
